import { Component, OnInit, OnDestroy } from '@angular/core';
import { IonicPage, NavParams, ModalController, AlertController } from 'ionic-angular';
import { Subscription } from 'rxjs';
import { IUser } from '../../models/user.model';
import { IPoll } from '../../models/poll.model';
import { PollData } from '../../providers/poll';
import { Store } from '@ngrx/store';
import { EventPollActiveComponent } from './components/event-poll-active/event-poll-active';
import { EventData } from '../../providers/event';
import { ENV } from '@app/env';

import * as clientSelectors from '../../ngrx/reducers/client.reducer';
import * as userSelectors from '../../ngrx/reducers/auth.reducer';
import { TranslateCodePipe } from '../../pipes/translate';
import { UtilitiesProvider } from '../../providers/utilities';

@IonicPage(
  {
    segment: 'poll-event/:eventId'
  }
)
@Component({
  selector: 'page-poll-event',
  templateUrl: 'poll-event.html',
})
export class PollEventPage implements OnInit, OnDestroy {

  public clientIdSubs: Subscription;
  public clientId: string;
  public currentUserSubs: Subscription;
  public currentUser: IUser;
  public activePollInformation: IPoll;

  public moduleStateSubs: Subscription;
  public moduleState: boolean;

  public eventId: string;

  private eventActivePollSubs: Subscription;
  private eventPollInformationSubs: Subscription;
  private canShowActiveEventPollToUserSubs: Subscription;
  private saveEventPollStatisticAndViewSubs: Subscription;

  constructor(
    private pollData: PollData,
    private store: Store<{}>,
    private modalController: ModalController,
    private alertController: AlertController,
    private navParams: NavParams,
    private eventData: EventData,
    private translatePipe: TranslateCodePipe,
    private utilities: UtilitiesProvider
  ) {
  }

  ngOnInit() {
    this.eventId = this.navParams.get('eventId');
    this.getClientId();
  }

  /**
   * Get clientId
   * 1.Get the clientId, then execute getCurrentUser() method.
   * @author Sebastián Guevara
  */
  private getClientId() {
    this.clientIdSubs = this.store.select(clientSelectors.getClientId).subscribe((clientId) => {
      if (clientId) {
        this.clientId = clientId;
        this.getCurrentUser();
      }
    })
  }

  /**
   *  Get the currentUser
   * 1. Get the currentUser, then execute getActivePoll() method.
   * @author Sebastián Guevara
   */
  private getCurrentUser() {
    this.currentUserSubs = this.store.select(userSelectors.getUser)
      .subscribe((user: IUser) => {
        if (user) {
          this.currentUser = user;
          this.getModuleState();
        }
      })
  }

  public getEventId(): string {
    return this.navParams.get('eventId');
  }

  public getModuleState() {
    this.moduleStateSubs = this.eventData.getModuleState(ENV.client, this.getEventId(), 'opinometer').subscribe((moduleState => {
      if (moduleState) {
        this.moduleState = true;
        this.getActivePoll();
      } else {
        this.moduleState = false;
      }
    }));

  }

  /**
   * Get the active base on the general interactivity
   * 1. If a poll is active, get the information about it.
   * @author Sebastián Guevara
   */
  private getActivePoll() {
    this.eventActivePollSubs = this.pollData.getEventActivePoll(this.clientId, this.eventId)
      .subscribe((pollId) => {
        if (pollId) {
          this.getPollInformation(<string>pollId);
        }
      });
  }

  /**
   * Get the poll information based on the pollId
   * @param pollId
   * @author Sebastián Guevara
   */
  public getPollInformation(pollId: string) {
    this.eventPollInformationSubs = this.pollData.getPollByClientId(this.clientId, pollId).subscribe((pollInformation: IPoll) => {
      this.activePollInformation = pollInformation;
      this.verifyUseAnswerByPoll(pollInformation);
    })
  }

  /**
   * Verify if the user has an answer for the poll
   * 1. If the user no has answers, and the poll is alert, open a alert that says "New poll is Active",
   * if the user click on the open button will execute the method openModal
   * 2. If user has answer, do not do anything.
   * @author Sebastián Guevara
   */
  public verifyUseAnswerByPoll(pollInformation: IPoll) {
    this.canShowActiveEventPollToUserSubs = this.pollData.canShowActiveEventPollToUser(this.clientId, this.eventId, pollInformation.general.id, this.currentUser.info.id).subscribe(async (pollPermissions) => {
      if (pollPermissions.show) {
        if (pollInformation.general.isAlert) {
          this.alertController.create({
            title: await this.translatePipe.transform('A new survey is active'),
            buttons: [
              {
                text: await this.utilities.translateTextOrObject('Open'),
                handler: () => {
                  this.openPollModal();
                  this.saveEventPollStatisticAndViewSubs = this.pollData.saveEventPollStatisticAndView(this.eventId, this.activePollInformation.general.id, this.currentUser.info.id).subscribe(() => { });
                  let elements: NodeListOf<Element> = document.getElementsByClassName("alert-md");
                  if (elements) {
                    for (let index = 0; index < elements.length; index++) {
                      const element = elements[index];
                      element.setAttribute("style", "display:none !important;");
                    }
                  }
                }
              },
              {
                text: 'Cancel',
                handler: () => {
                  this.saveEventPollStatisticAndViewSubs = this.pollData.saveEventPollStatisticAndView(this.eventId, this.activePollInformation.general.id, this.currentUser.info.id).subscribe(() => { });
                  let elements: NodeListOf<Element> = document.getElementsByClassName("alert-md");
                  if (elements) {
                    for (let index = 0; index < elements.length; index++) {
                      const element = elements[index];
                      element.setAttribute("style", "display:none !important;");
                    }
                  }
                }
              }
            ]
          }).present();
        }
      }
    })
  }


  /**
   * Open the pollActive Modal
   * @author Sebastián Guevara
   */
  public openPollModal() {
    this.modalController.create(EventPollActiveComponent, {
      clientId: this.clientId,
      eventId: this.eventId,
      pollId: this.activePollInformation.general.id
    }, {
        enableBackdropDismiss: false
      }).present();
  }

  ngOnDestroy() {
    if (this.clientIdSubs) this.clientIdSubs.unsubscribe();
    if (this.currentUserSubs) this.currentUserSubs.unsubscribe();
    if (this.moduleStateSubs) this.moduleStateSubs.unsubscribe();
    if (this.eventActivePollSubs) this.eventActivePollSubs.unsubscribe();
    if (this.eventPollInformationSubs) this.eventPollInformationSubs.unsubscribe();
    if (this.canShowActiveEventPollToUserSubs) this.canShowActiveEventPollToUserSubs.unsubscribe();
    if (this.saveEventPollStatisticAndViewSubs) this.saveEventPollStatisticAndViewSubs.unsubscribe();
  }

}



// WEBPACK FOOTER //
// ./src/pages/poll-event/poll-event.ts